import { SUMMARY_PDF_2027_1 } from "./summary-pdf-sales";
export interface VersionInfo {
  version: string;
  date: string;
  releaseAt?: string;
  description: string;
  blobPath: string;
  detailAvailable?: boolean;
}

export type PdfVersionChangeType =
  | "오류 수정"
  | "내용 추가"
  | "표기 통일"
  | "구조 개선";

export type PdfVariant = "base" | "q" | "h";

export interface PdfVersionLocation {
  variant: PdfVariant;
  before: string;
  after: string;
}

export interface PdfVersionScreenshot {
  src: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
  highlights: PdfVersionScreenshotHighlight[];
}

/** 이미지 원본 크기와 무관하게 유지되는 변경 지점 사각형(0~100%). */
export interface PdfVersionScreenshotHighlight {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfVersionChange {
  id: string;
  type: PdfVersionChangeType;
  title: string;
  summary: string;
  before: string;
  after: string;
  locations: PdfVersionLocation[];
  screenshots: {
    before: PdfVersionScreenshot;
    after: PdfVersionScreenshot;
  };
}

export interface PdfVersionDetail {
  productId: string;
  productName: string;
  version: string;
  /** 생략하면 같은 상품의 이전 버전과 비교한다. */
  comparedProductId?: string;
  comparedVersion: string;
  date: string;
  releaseAt: string;
  introduction: string;
  changes: PdfVersionChange[];
}

export const PDF_VARIANT_LABELS: Record<PdfVariant, string> = {
  base: "기본",
  q: "Q 가림막",
  h: "H 가로",
};

export const versionData: Record<string, VersionInfo[]> = {
  ...Object.fromEntries(SUMMARY_PDF_2027_1.products.map(product => [product.id, product.publication?.versions ?? []])),
  "26-3-compressed-pdf": [
    {
      version: "1-0",
      date: "2026.09.16",
      releaseAt: "2026-09-16T00:00:00+09:00",
      description: "2026년 3회 첫 출시본. 학습 범위·우선순위 갱신, 프리픽스·서브넷 마스크 계산 보강, 전체 주소·할당 가능한 호스트 수 구분 및 테스트 오라클 비교표 추가.",
      blobPath: "pdfs/26-3-compressed/26-3-compressed_v1-0.pdf",
      detailAvailable: true,
    },
    {
      version: "1-0_Q",
      date: "2026.09.16",
      description: "정답 키워드를 가린 Q 버전 (v1-0)",
      blobPath: "pdfs/26-3-compressed/26-3-compressed_v1-0_q.pdf",
    },
    {
      version: "1-0_H",
      date: "2026.09.16",
      description: "태블릿 필기용 H 가로 버전 (v1-0)",
      blobPath: "pdfs/26-3-compressed/26-3-compressed_v1-0_h.pdf",
    },
  ],
  "26-3-coding-pdf": [
    {
      version: "1-0",
      date: "2026.09.16",
      releaseAt: "2026-09-16T00:00:00+09:00",
      description: "2026년 3회 첫 출시본. 포인터 값·주소 전달, 이진 트리 순회, CREATE DOMAIN 보강 및 ASCII 정정.",
      blobPath: "pdfs/26-3-coding/26-3-coding_v1-0.pdf",
      detailAvailable: true,
    },
    {
      version: "1-0_Q",
      date: "2026.09.16",
      description: "실행 결과·정답 토큰을 가린 Q 버전 (v1-0)",
      blobPath: "pdfs/26-3-coding/26-3-coding_v1-0_q.pdf",
    },
    {
      version: "1-0_H",
      date: "2026.09.16",
      description: "태블릿 필기용 H 가로 버전 (v1-0)",
      blobPath: "pdfs/26-3-coding/26-3-coding_v1-0_h.pdf",
    },
  ],
  "26-1-compressed-pdf": [
    {
      version: "2-2",
      date: "2026.07.17",
      releaseAt: "2026-07-17T11:00:00+09:00",
      description:
        "라우팅 프로토콜 분류 도식에서 IGP(RIP·OSPF)·EGP(BGP)가 정적 라우팅 하위처럼 보이던 오류를 동적 라우팅에서 분기하도록 계층 수정",
      blobPath: "pdfs/26-1-compressed/26-2-compressed_v2-2.pdf",
      detailAvailable: true,
    },
    {
      version: "2-2_Q",
      date: "2026.07.17",
      description: "키워드가 가려져 있는 버전(v2-2 반영)",
      blobPath: "pdfs/26-1-compressed/26-2-compressed_v2-2_q.pdf",
    },
    {
      version: "2-2_H",
      date: "2026.07.17",
      description: "태블릿 유저를 위한 가로 버전(v2-2 반영)",
      blobPath: "pdfs/26-1-compressed/26-2-compressed_v2-2_h.pdf",
    },
    {
      version: "2-1",
      date: "2026.07.14",
      releaseAt: "2026-07-14T20:00:00+09:00",
      description:
        "물리적 설계 산출물 '테이블 정의서' 용어 오류 수정, HDLC 프레임·사용가능한 IP 주소 갯수 계산·chmod 기출 표시 추가, 체크리스트 출제확률 표기 정리",
      blobPath: "pdfs/26-1-compressed/26-2-compressed_v2-1.pdf",
    },
    {
      version: "2-1_Q",
      date: "2026.07.14",
      description: "키워드가 가려져 있는 버전(v2-1 반영)",
      blobPath: "pdfs/26-1-compressed/26-2-compressed_v2-1_q.pdf",
    },
    {
      version: "2-1_H",
      date: "2026.07.14",
      description: "태블릿 유저를 위한 가로 버전(v2-1 반영)",
      blobPath: "pdfs/26-1-compressed/26-2-compressed_v2-1_h.pdf",
    },
    {
      version: "2-0",
      date: "2026.07.09",
      releaseAt: "2026-07-09T15:15:00+09:00",
      description: "2026년 2회 예상 실기 이론 문제 범위 반영",
      blobPath: "pdfs/26-1-compressed/26-2-compressed_v2-0.pdf",
    },
    {
      version: "1-1",
      date: "2026.04.06",
      description:
        "응집도/결합도 암기법(내공외제스자, 우논시절통순기) 및 이미지 추가, 키 무결성 내용 오류 수정, 유닉스 명령어 URL 오류 수정",
      blobPath: "pdfs/26-1-compressed/26-1-compressed_v1-1.pdf",
    },
    {
      version: "1-1_Q",
      date: "2026.04.06",
      description: "키워드가 가려져 있는 버전(v1-1 반영)",
      blobPath: "pdfs/26-1-compressed/26-1-compressed_v1-1_q.pdf",
    },
    {
      version: "1-1_H",
      date: "2026.04.06",
      description: "태블릿 유저를 위한 가로 버전(v1-1 반영)",
      blobPath: "pdfs/26-1-compressed/26-1-compressed_v1-1_h.pdf",
    },
    {
      version: "1-0",
      date: "2026.01.12",
      description: "26년 1회 실기 이론 압축 요약 PDF - 최초 출시",
      blobPath: "pdfs/26-1-compressed/26-1-compressed_v1-0.pdf",
    },
    {
      version: "1-0_Q",
      date: "2026.01.12",
      description: "키워드가 가려져 있는 버전",
      blobPath: "pdfs/26-1-compressed/26-1-compressed_v1-0_q.pdf",
    },
    {
      version: "1-0_H",
      date: "2026.01.12",
      description: "태블릿 유저를 위한 가로 버전",
      blobPath: "pdfs/26-1-compressed/26-1-compressed_v1-0_h.pdf",
    },
  ],
  "26-1-coding-pdf": [
    {
      version: "2-2",
      date: "2026.07.17",
      releaseAt: "2026-07-17T11:00:00+09:00",
      description:
        "코딩 다이어그램 214개를 실제 컴파일·실행으로 전수 점검. 제네릭 타입 소거(호출 구조·정답 정정), C 조건문 값 오류(가림막 오답 노출 포함), 파이썬 딕셔너리 뷰 객체(dict_keys) 표기, 연산자 우선순위 표, C 컴파일 오류 코드, SQL 외부조인 결과표, 자바 다이어그램 오류를 정정하고 파이썬 출력 표기를 통일",
      blobPath: "pdfs/26-1-coding/26-2-coding_v2-2.pdf",
      detailAvailable: true,
    },
    {
      version: "2-2_Q",
      date: "2026.07.17",
      description: "키워드가 가려져 있는 버전(v2-2 반영)",
      blobPath: "pdfs/26-1-coding/26-2-coding_v2-2_q.pdf",
    },
    {
      version: "2-2_H",
      date: "2026.07.17",
      description: "태블릿 유저를 위한 가로 버전(v2-2 반영)",
      blobPath: "pdfs/26-1-coding/26-2-coding_v2-2_h.pdf",
    },
    {
      version: "2-1",
      date: "2026.07.14",
      releaseAt: "2026-07-14T20:00:00+09:00",
      description:
        "비트 연산자(~) 부호 비트 반전 설명 보강 및 계산 결과 오류 수정(~5 = −6), 추상 클래스 '일반 메서드가 추상 메서드 호출' 추가, 파이썬 딕셔너리 .get(key, 기본값) 추가, SQL 뷰를 통한 데이터 입력·수정 및 WITH CHECK OPTION 추가",
      blobPath: "pdfs/26-1-coding/26-2-coding_v2-1.pdf",
    },
    {
      version: "2-1_Q",
      date: "2026.07.14",
      description: "키워드가 가려져 있는 버전(v2-1 반영)",
      blobPath: "pdfs/26-1-coding/26-2-coding_v2-1_q.pdf",
    },
    {
      version: "2-1_H",
      date: "2026.07.14",
      description: "태블릿 유저를 위한 가로 버전(v2-1 반영)",
      blobPath: "pdfs/26-1-coding/26-2-coding_v2-1_h.pdf",
    },
    {
      version: "2-0",
      date: "2026.07.09",
      releaseAt: "2026-07-09T15:15:00+09:00",
      description: "2026년 2회 예상 실기 코딩 문제 범위 반영(C·Java·Python·SQL)",
      blobPath: "pdfs/26-1-coding/26-2-coding_v2-0.pdf",
    },
    {
      version: "1-0",
      date: "2026.04.15",
      description:
        "C언어 100% 커버, Java 100% 커버, Python 100% 커버 (모든 언어 범위 100% 커버 완료)",
      blobPath: "pdfs/26-1-coding/26-1-coding_v1-0.pdf",
    },
    {
      version: "1-0_Q",
      date: "2026.04.15",
      description: "키워드가 가려져 있는 버전(v1-0 반영)",
      blobPath: "pdfs/26-1-coding/26-1-coding_v1-0_q.pdf",
    },
    {
      version: "1-0_H",
      date: "2026.04.15",
      description: "태블릿 유저를 위한 가로 버전(v1-0 반영)",
      blobPath: "pdfs/26-1-coding/26-1-coding_v1-0_h.pdf",
    },
    {
      version: "0-2",
      date: "2026.04.11",
      description:
        "Python 추가 (C언어 95% 범위 커버, Java 80% 범위 커버, Python 100% 범위 커버)",
      blobPath: "pdfs/26-1-coding/26-1-coding_v0-2.pdf",
    },
    {
      version: "0-1",
      date: "2026.04.06",
      description:
        "C언어 추가 및 Java 신규 업데이트 (C언어 95% 범위 커버, Java 80% 범위 커버, Python 0% 범위 커버)",
      blobPath: "pdfs/26-1-coding/26-1-coding_v0-1.pdf",
    },
    {
      version: "0-0",
      date: "2026.03.26",
      description:
        "최초 출시 (C언어 90% 범위 커버, Java 0% 범위 커버, Python 0% 범위 커버)",
      blobPath: "pdfs/26-1-coding/26-1-coding_v0-0.pdf",
    },
  ],
  "25-3-super-compressed-pdf": [
    {
      version: "1-2",
      date: "2025.11.05",
      description: "디자인 패턴 키워드 개선",
      blobPath: "pdfs/25-3-super-compressed/25-3-super-compressed_v1-2.pdf",
    },
    {
      version: "1-1",
      date: "2025.11.01",
      description: "25년 3회 실기 이론 초압축 요약 PDF - 최초 출시",
      blobPath: "pdfs/25-3-super-compressed/25-3-super-compressed_v1-1.pdf",
    },
  ],
  "25-3-compressed-pdf": [
    {
      version: "3-1",
      date: "2025.11.05",
      description: "디자인 패턴 키워드 개선, 데이터링크 - HDLC 동작모드 오타 수정",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v3-1.pdf",
    },
    {
      version: "3-1_Q",
      date: "2025.11.05",
      description: "키워드가 가려져 있는 버전(v3-1 반영)",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v3-1_q.pdf",
    },
    {
      version: "3-1_H",
      date: "2025.11.05",
      description: "태블릿 유저를 위한 가로 버전(v3-1 반영)",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v3-1_h.pdf",
    },
    {
      version: "3-0",
      date: "2025.11.01",
      description: "SQL문 추가, 스토리지/네트워크 전송 방식 추가, RIP 경로문제 링크 추가",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v3-0.pdf",
    },
    {
      version: "3-0_Q",
      date: "2025.11.01",
      description: "키워드가 가려져 있는 버전",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v3-0_q.pdf",
    },
    {
      version: "3-0_H",
      date: "2025.11.01",
      description: "태블릿 유저를 위한 가로 버전",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v3-0_h.pdf",
    },
    {
      version: "2-2",
      date: "2025.10.22",
      description: "스니핑(보안/신기술 - 네트워크 공격) 추가, 링크 오류 수정",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v2-2.pdf",
    },
    {
      version: "2-1",
      date: "2025.10.19",
      description: "과목 및 범위 별 정처기 감자 페이지 링크 추가, PDF 책갈피 추가",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v2-1.pdf",
    },
    {
      version: "1-0",
      date: "2025.10.15",
      description: "최초 버전 출시",
      blobPath: "pdfs/25-3-compressed/25-3-compressed_v1.pdf",
    },
  ],
  "25-2-super-compressed-pdf": [
    {
      version: "1-0",
      date: "2025.7.19",
      description: "25년 2회 실기 이론 초압축 요약 PDF - 업데이트 종료",
      blobPath: "pdfs/25-2-super-compressed.pdf",
    },
  ],
};


/** 본편(가림막·가로 변형이 아닌) 숫자 버전인지. */
export function isNumericPdfVersion(version: string): boolean {
  return /^\d+-\d+$/.test(version);
}

/** "2-1_Q" → "2-1". */
export function versionBaseLabel(version: string): string {
  return version.split("_", 1)[0];
}

function numericVersionBase(version: string): number[] {
  return versionBaseLabel(version)
    .split("-")
    .map((part) => Number(part));
}

export function comparePdfVersions(a: string, b: string): number {
  const aParts = numericVersionBase(a);
  const bParts = numericVersionBase(b);
  const length = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < length; i++) {
    const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export function getPdfVersions(productId: string): readonly VersionInfo[] {
  // "constructor"·"__proto__" 같은 프로토타입 키를 상품으로 해석하지 않는다.
  return Object.hasOwn(versionData, productId) ? versionData[productId] : [];
}

export function getLatestPdfVersionInfo(productId: string): VersionInfo | null {
  const versions = getPdfVersions(productId);
  if (!versions.length) return null;
  const numericVersions = versions.filter((version) =>
    isNumericPdfVersion(version.version),
  );
  if (numericVersions.length === 0) return null;
  return numericVersions.reduce((latest, version) =>
    comparePdfVersions(version.version, latest.version) > 0 ? version : latest,
  );
}

/**
 * 다운로드할 버전을 해석한다. `version`이 없으면 숫자 본편 최신본,
 * 있으면 정확히 같은 버전(`_Q`/`_H` 포함)만 반환하고, 없으면 null.
 */
export function resolvePdfVersion(
  productId: string,
  version?: string | null,
): VersionInfo | null {
  if (!version) return getLatestPdfVersionInfo(productId);
  return getPdfVersions(productId).find((v) => v.version === version) ?? null;
}

export function hasPdfVersionAtLeast(
  downloadedVersions: readonly string[] | null | undefined,
  latestVersion: string,
): boolean {
  return (downloadedVersions ?? []).some(
    (downloadedVersion) =>
      isNumericPdfVersion(downloadedVersion) &&
      comparePdfVersions(downloadedVersion, latestVersion) >= 0,
  );
}

export function getLatestPdfVersionDate(productId: string): string | null {
  return getLatestPdfVersionInfo(productId)?.date ?? null;
}

export function getPdfVersionDetailHref(productId: string, version: string): string {
  return `/my/purchases/pdf/${encodeURIComponent(productId)}/${encodeURIComponent(version)}`;
}
